import {
    useState,
    useEffect,
    useRef,
    useMemo,
    useImperativeHandle,
    forwardRef,
} from "react";
import { CircularProgress, Link, Grid } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faExternalLinkAlt,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

import * as Database from "utils/database.utils";
import { cyclicalityOptions, gicsSectorOptions } from "assets/data/lists";

import Table from "components/Common/Table/Table";
import TableCell from "components/Common/TableCell/TableCell";
import CompanyForm, {
    CompanyFormRef,
    CompanyFormData,
    CompanyLink,
    SelectOption,
    useBbgEditableFields,
    useBbgFetch,
    type CustomCategory,
} from "../CompanyForm";
import InfoTabContainer from "./InfoTab.styles";

type Props = {
    companyData: any;
    loading: boolean;
    editMode: boolean;
    onBbgFetchingChange?: (isFetching: boolean) => void;
};

export type InfoTabRef = {
    save: () => Promise<any>;
    validate: () => Record<string, string>;
    setFieldErrors: (errors: Record<string, string>) => void;
};

const InfoTab = forwardRef<InfoTabRef, Props>(
    ({ companyData, loading, editMode, onBbgFetchingChange }, ref) => {
        const formRef = useRef<CompanyFormRef>(null);
        const [fieldErrors, setFieldErrors] = useState<Record<string, string>>(
            {}
        );
        const [linkTypeOptions, setLinkTypeOptions] = useState<SelectOption[]>(
            []
        );
        const [analystOptions, setAnalystOptions] = useState<SelectOption[]>(
            []
        );
        const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
        const [sectorOptions, setSectorOptions] = useState<SelectOption[]>([]);
        const [riskOptions, setRiskOptions] = useState<SelectOption[]>([]);
        const [customCategoryMap, setCustomCategoryMap] = useState<
            Map<number, { name: string; items: Map<number, string> }>
        >(new Map());
        const bbg = useBbgFetch();
        const bbgEditable = useBbgEditableFields();

        useEffect(() => {
            onBbgFetchingChange?.(bbg.status === "loading");
        }, [bbg.status, onBbgFetchingChange]);

        useEffect(() => {
            const parseOptions = async (
                res: Response
            ): Promise<SelectOption[]> => {
                if (!res.ok) return [];
                const data = await res.json();
                return data.data.map((item: { name: string; id: string }) => ({
                    label: item.name,
                    value: String(item.id),
                }));
            };

            const fetchOptions = async () => {
                try {
                    const [linkTypesRes, analystsRes, teamsRes] =
                        await Promise.all([
                            Database.get("api/company/link-types"),
                            Database.get("api/company/analysts"),
                            Database.get(
                                "api/company/teams?usedInCompanyManagement=true"
                            ),
                        ]);

                    const [linkTypes, analysts, teams] = await Promise.all([
                        parseOptions(linkTypesRes),
                        parseOptions(analystsRes),
                        parseOptions(teamsRes),
                    ]);

                    setLinkTypeOptions(linkTypes);
                    setAnalystOptions(analysts);
                    setTeamOptions(teams);
                } catch (err) {
                    console.error("Error fetching options:", err);
                }
            };

            fetchOptions();
        }, []);

        useEffect(() => {
            if (!companyData) return;

            const fetchCategories = async () => {
                try {
                    const catsRes = await Database.get("api/company/categories");
                    if (!catsRes.ok) return;

                    const catsData = await catsRes.json();
                    const catMap = new Map<number, { name: string; items: Map<number, string> }>();

                    await Promise.all(
                        catsData.data.map(async (cat: any) => {
                            const itemsMap = new Map<number, string>();
                            const itemsRes = await Database.get(`api/company/category-items/${cat.id}`);
                            if (itemsRes.ok) {
                                const itemsData = await itemsRes.json();
                                for (const item of itemsData.data) {
                                    itemsMap.set(item.id, item.name);
                                }
                            }
                            catMap.set(cat.id, { name: cat.name, items: itemsMap });
                        })
                    );

                    const toOptions = (catId: number): SelectOption[] =>
                        Array.from(catMap.get(catId)?.items.entries() ?? []).map(
                            ([id, name]) => ({ label: name, value: String(id) })
                        );

                    setSectorOptions(toOptions(1));
                    setRiskOptions(toOptions(2));
                    setCustomCategoryMap(catMap);
                } catch (err) {
                    console.error("Error fetching categories:", err);
                }
            };

            fetchCategories();
        }, [companyData]);

        const initialFormData = useMemo((): CompanyFormData | undefined => {
            if (!companyData) return undefined;
            return {
                name: companyData.name || "",
                ticker: companyData.ticker || "",
                team_id: companyData.team_id ? String(companyData.team_id) : "",
                analyst_id: companyData.analyst_id || "",
                gef_focus_list: companyData.gef_focus_list || false,
                aef_focus_list: companyData.aef_focus_list || false,
                link_to_model: companyData.link_to_model || "",
                ipo_flag: companyData.ipo_flag || false,
                cyclicality: companyData.cyclicality || "",
                isin: companyData.isin || "",
                sedol: companyData.sedol || "",
                cusip: companyData.cusip || "",
                eu_composite_ticker: companyData.eu_composite_ticker || "",
                sector_risk: companyData.sector_risk || "",
                genim_gics_sector: companyData.genim_gics_sector || "",
                gef_max_wgt_adj: companyData.gef_max_wgt_adj?.toString() || "",
                packaged_software: companyData.packaged_software || false,
                med_tools: companyData.med_tools || false,
                private_flag: companyData.private_flag || false,
            };
        }, [companyData]);

        const initialLinks = useMemo((): CompanyLink[] => {
            if (
                !companyData?.company_links ||
                companyData.company_links.length === 0
            ) {
                return [{ link_type_value: "", link_type_id: null, url: "" }];
            }
            return companyData.company_links.map((link: any) => ({
                link_type_value:
                    linkTypeOptions.find(
                        (opt) => opt.value === String(link.link_type_id)
                    )?.label || "",
                link_type_id: String(link.link_type_id),
                url: link.url,
            }));
        }, [companyData, linkTypeOptions]);

        const initialSectors = useMemo(() => {
            if (!companyData?.company_category_items) return undefined;
            const items = companyData.company_category_items
                .filter((item: any) => item.category_id === 1)
                .map((item: any) => ({
                    sector: String(item.category_item_id),
                    percentage: item.percentage?.toString() || "",
                }));
            return items.length > 0 ? items : undefined;
        }, [companyData]);

        const initialRisks = useMemo(() => {
            if (!companyData?.company_category_items) return undefined;
            const items = companyData.company_category_items
                .filter((item: any) => item.category_id === 2)
                .map((item: any) => ({
                    risk: String(item.category_item_id),
                    percentage: item.percentage?.toString() || "",
                }));
            return items.length > 0 ? items : undefined;
        }, [companyData]);

        const initialCustomCategories = useMemo(():
            | CustomCategory[]
            | undefined => {
            if (
                !companyData?.company_category_items ||
                customCategoryMap.size === 0
            )
                return undefined;

            const customItems = companyData.company_category_items.filter(
                (item: any) =>
                    item.category_id !== 1 &&
                    item.category_id !== 2 &&
                    customCategoryMap.has(item.category_id)
            );

            if (customItems.length === 0) return undefined;

            const grouped = new Map<number, any[]>();
            for (const item of customItems) {
                const existing = grouped.get(item.category_id) || [];
                existing.push(item);
                grouped.set(item.category_id, existing);
            }

            const result: CustomCategory[] = [];
            Array.from(grouped.entries()).forEach(([categoryId, items]) => {
                const catInfo = customCategoryMap.get(categoryId);
                if (!catInfo) return;
                result.push({
                    category_name: catInfo.name,
                    category_id: String(categoryId),
                    items: items.map((item: any) => ({
                        item_name:
                            catInfo.items.get(item.category_item_id) || "",
                        item_id: String(item.category_item_id),
                        percentage: item.percentage?.toString() || "",
                    })),
                });
            });

            return result.length > 0 ? result : undefined;
        }, [companyData, customCategoryMap]);

        const handleRefetchBbg = async () => {
            const currentTicker = formRef.current?.getFormData().ticker;
            if (!currentTicker) return;

            setFieldErrors({});
            const result = await bbg.fetch(currentTicker, companyData?.id);

            if (result.success) {
                formRef.current?.updateFormData(result.data);
                bbgEditable.disableAll();
            } else {
                setFieldErrors({ ticker: result.error });
            }
        };

        useImperativeHandle(ref, () => ({
            validate: () => {
                if (!formRef.current) return {};
                const errors = formRef.current.validate();
                setFieldErrors(errors);
                return errors;
            },
            save: async () => {
                if (!formRef.current) return null;
                setFieldErrors({});
                return await formRef.current.prepareSubmitData();
            },
            setFieldErrors: (errors: Record<string, string>) => {
                setFieldErrors(errors);
            },
        }));

        const getLabel = (
            options: SelectOption[],
            value: string | null
        ): string => {
            if (!value) return "-";
            return options.find((o) => o.value === value)?.label || value;
        };

        const formatPercentage = (value: number | null): string => {
            if (value === null || value === undefined) return "-";
            return `${value}%`;
        };

        if (loading) {
            return (
                <InfoTabContainer>
                    <div className="loadingContainer">
                        <CircularProgress size={40} />
                    </div>
                </InfoTabContainer>
            );
        }

        if (!companyData) {
            return (
                <InfoTabContainer>
                    <div className="errorContainer">
                        <p>Failed to load company data</p>
                    </div>
                </InfoTabContainer>
            );
        }

        if (editMode) {
            return (
                <InfoTabContainer>
                    <CompanyForm
                        ref={formRef}
                        initialData={initialFormData}
                        initialLinks={initialLinks}
                        initialSectors={initialSectors}
                        initialRisks={initialRisks}
                        initialCustomCategories={initialCustomCategories}
                        showBbgFields
                        isEditMode
                        bbgStatus={bbg.status}
                        bbgEditableFields={bbgEditable.fields}
                        onEnableBbgField={bbgEditable.enableField}
                        onRetryBbgFetch={handleRefetchBbg}
                        fieldErrors={fieldErrors}
                        setFieldErrors={setFieldErrors}
                    />
                </InfoTabContainer>
            );
        }

        const sectors = companyData.company_category_items?.filter(
            (item: any) => item.category_id === 1
        );
        const risks = companyData.company_category_items?.filter(
            (item: any) => item.category_id === 2
        );

        const customCategoriesGrouped = new Map<
            number,
            { name: string; items: any[] }
        >();
        companyData.company_category_items
            ?.filter(
                (item: any) =>
                    item.category_id !== 1 &&
                    item.category_id !== 2 &&
                    customCategoryMap.has(item.category_id)
            )
            .forEach((item: any) => {
                const catInfo = customCategoryMap.get(item.category_id);
                if (!catInfo) return;
                const existing = customCategoriesGrouped.get(
                    item.category_id
                ) || { name: catInfo.name, items: [] };
                existing.items.push({
                    ...item,
                    item_name:
                        catInfo.items.get(item.category_item_id) ||
                        String(item.category_item_id),
                });
                customCategoriesGrouped.set(item.category_id, existing);
            });

        const basicInfoAndIdentifiersColumns = [
            {
                id: 1,
                title: "TICKER",
                sortable: false,
                value: "ticker",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 2,
                title: "COMPANY NAME",
                sortable: false,
                value: "name",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 3,
                title: "TEAM",
                sortable: false,
                value: "team_id",
                cell: (data: number) => (
                    <TableCell
                        data={getLabel(teamOptions, data ? String(data) : null)}
                    />
                ),
                show: true,
            },
            {
                id: 4,
                title: "ANALYST",
                sortable: false,
                value: "analyst_id",
                cell: (data: string) => (
                    <TableCell data={getLabel(analystOptions, data)} />
                ),
                show: true,
            },
            {
                id: 5,
                title: "ISIN",
                sortable: false,
                value: "isin",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 6,
                title: "SEDOL ID",
                sortable: false,
                value: "sedol",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 7,
                title: "CUSIP ID",
                sortable: false,
                value: "cusip",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 8,
                title: "EU COMPOSITE TICKER",
                sortable: false,
                value: "eu_composite_ticker",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
            {
                id: 9,
                title: "SECTOR/RISK",
                sortable: false,
                value: "sector_risk",
                cell: (data: string) => <TableCell data={data || "-"} />,
                show: true,
            },
        ];

        const exposureAndFlagsColumns = [
            {
                id: 1,
                title: "GenIM GICS SECTOR",
                sortable: false,
                value: "genim_gics_sector",
                cell: (data: string) => (
                    <TableCell data={getLabel(gicsSectorOptions, data)} />
                ),
                show: true,
            },
            {
                id: 2,
                title: "CYCLICALITY",
                sortable: false,
                value: "cyclicality",
                cell: (data: string) => (
                    <TableCell data={getLabel(cyclicalityOptions, data)} />
                ),
                show: true,
            },
            {
                id: 3,
                title: "GEF MAX WGT ADJ",
                sortable: false,
                value: "gef_max_wgt_adj",
                cell: (data: number) => (
                    <TableCell data={formatPercentage(data)} />
                ),
                show: true,
            },
            {
                id: 4,
                title: "GEF FL",
                sortable: false,
                value: "gef_focus_list",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
            {
                id: 5,
                title: "AEF FL",
                sortable: false,
                value: "aef_focus_list",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
            {
                id: 6,
                title: "PACKAGED SOFTWARE",
                sortable: false,
                value: "packaged_software",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
            {
                id: 7,
                title: "MED TOOLS",
                sortable: false,
                value: "med_tools",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
            {
                id: 8,
                title: "IPO",
                sortable: false,
                value: "ipo_flag",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
            {
                id: 9,
                title: "PRIVATE",
                sortable: false,
                value: "private_flag",
                cell: (data: boolean) =>
                    data ? (
                        <FontAwesomeIcon
                            className="checkIcon"
                            icon={faCheckCircle}
                            fixedWidth
                        />
                    ) : null,
                show: true,
            },
        ];

        const pDriveLinkColumns = [
            {
                id: 1,
                title: "P DRIVE LINK TO COMPANY MODEL",
                sortable: false,
                value: "link_to_model",
                cell: (data: string) =>
                    data ? (
                        <Link
                            href={data}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="linkUrl"
                        >
                            {data}
                            <FontAwesomeIcon
                                icon={faExternalLinkAlt}
                                size="sm"
                            />
                        </Link>
                    ) : (
                        <TableCell data="-" />
                    ),
                show: true,
            },
        ];

        const externalLinksColumns = [
            {
                id: 1,
                title: "EXTERNAL LINKS",
                sortable: false,
                value: "url",
                fullDataRow: true,
                cell: (row: any) => (
                    <Link
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="linkUrl"
                    >
                        {getLabel(linkTypeOptions, String(row.link_type_id))}
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                    </Link>
                ),
                show: true,
            },
        ];

        const sectorsColumns = [
            {
                id: 1,
                title: "ECONOMIC SECTORS",
                sortable: false,
                value: "category_item_id",
                fullDataRow: true,
                cell: (row: any) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <span>
                            {getLabel(
                                sectorOptions,
                                String(row.category_item_id)
                            )}
                        </span>
                        <span>{row.percentage}%</span>
                    </div>
                ),
                show: true,
            },
        ];

        const risksColumns = [
            {
                id: 1,
                title: "THEMATIC RISKS",
                sortable: false,
                value: "category_item_id",
                fullDataRow: true,
                cell: (row: any) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <span>
                            {getLabel(
                                riskOptions,
                                String(row.category_item_id)
                            )}
                        </span>
                        <span>{row.percentage}%</span>
                    </div>
                ),
                show: true,
            },
        ];

        const externalLinksData =
            companyData.company_links?.map((link: any, index: number) => ({
                ...link,
                tableID: link.id || index,
            })) || [];

        const sectorsData =
            sectors?.map((item: any) => ({
                ...item,
                tableID: item.category_item_id,
            })) || [];

        const risksData =
            risks?.map((item: any) => ({
                ...item,
                tableID: item.category_item_id,
            })) || [];

        return (
            <InfoTabContainer>
                <Table
                    data={[{ ...companyData, tableID: 1 }]}
                    columns={basicInfoAndIdentifiersColumns}
                    filterQuery=""
                    showHeader
                />

                <Table
                    data={[{ ...companyData, tableID: 1 }]}
                    columns={exposureAndFlagsColumns}
                    filterQuery=""
                    showHeader
                />

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Table
                            data={[{ ...companyData, tableID: 1 }]}
                            columns={pDriveLinkColumns}
                            filterQuery=""
                            showHeader
                        />
                    </Grid>

                    {externalLinksData.length > 0 && (
                        <Grid item xs={6}>
                            <Table
                                data={externalLinksData}
                                columns={externalLinksColumns}
                                filterQuery=""
                                showHeader
                            />
                        </Grid>
                    )}
                </Grid>

                <Grid container spacing={2}>
                    {sectorsData.length > 0 && (
                        <Grid item xs={3}>
                            <Table
                                data={sectorsData}
                                columns={sectorsColumns}
                                filterQuery=""
                                showHeader
                            />
                        </Grid>
                    )}

                    {risksData.length > 0 && (
                        <Grid item xs={3}>
                            <Table
                                data={risksData}
                                columns={risksColumns}
                                filterQuery=""
                                showHeader
                            />
                        </Grid>
                    )}

                    {Array.from(customCategoriesGrouped.entries()).map(
                        ([categoryId, { name, items }]) => (
                            <Grid item xs={3} key={categoryId}>
                                <Table
                                    data={items.map((item: any) => ({
                                        ...item,
                                        tableID: item.category_item_id,
                                    }))}
                                    columns={[
                                        {
                                            id: 1,
                                            title: name.toUpperCase(),
                                            sortable: false,
                                            value: "item_name",
                                            fullDataRow: true,
                                            cell: (row: any) => (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: 8,
                                                    }}
                                                >
                                                    <span>{row.item_name}</span>
                                                    <span>
                                                        {row.percentage}%
                                                    </span>
                                                </div>
                                            ),
                                            show: true,
                                        },
                                    ]}
                                    filterQuery=""
                                    showHeader
                                />
                            </Grid>
                        )
                    )}
                </Grid>
            </InfoTabContainer>
        );
    }
);

InfoTab.displayName = "InfoTab";

export default InfoTab;
